import React, { memo } from 'react';
import { AppNode, NodeType } from '@/types';
import { useNodeStore } from '@/stores/nodeStore';
import { useProjectStore } from '@/stores/projectStore';
import { fetchImageForCanvas, normalizeAssetUrl } from '@/utils/assetUtils';
import { projectApi } from '@/services/projectApi';
import { toast } from 'sonner';
import { ArrowLeft, Grid3x3, Sparkles, MonitorUp, ChevronDown } from 'lucide-react';
import { InverseScaleBox } from './InverseScaleBox';

const DEFAULT_ASPECT_RATIO = '16:9';

function cropImageCell(img: HTMLImageElement, row: number, col: number, rows: number, cols: number): string {
  const canvas = document.createElement('canvas');
  const cellW = Math.floor(img.width / cols);
  const cellH = Math.floor(img.height / rows);
  canvas.width = cellW;
  canvas.height = cellH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No canvas context');
  ctx.drawImage(
    img,
    Math.floor((col - 1) * cellW),
    Math.floor((row - 1) * cellH),
    cellW,
    cellH,
    0,
    0,
    cellW,
    cellH
  );
  return canvas.toDataURL('image/png');
}

async function cropCell(imgUrl: string, row: number, col: number, rows: number, cols: number): Promise<string> {
  const img = await fetchImageForCanvas(imgUrl);
  return cropImageCell(img, row, col, rows, cols);
}

/**
 * 将 Base64 Data URL 上传到后端存储，返回持久化的服务器 URL
 */
async function uploadDataUrlToServer(
  dataUrl: string,
  projectId: string,
  nodeId: string,
  cellId: string
): Promise<string> {
  // 将 data URL 转换为 Blob
  const res = await fetch(dataUrl);
  const blob = await res.blob();

  // 构造文件名
  const filename = `gridsplit_${nodeId}_${cellId}.png`;
  const file = new File([blob], filename, { type: 'image/png' });

  // 上传到后端
  const asset = await projectApi.uploadAsset(projectId, file, {
    source: 'grid_split',
    node_id: nodeId,
    cell: cellId,
  });

  return normalizeAssetUrl(asset.url);
}

interface GridSplitBarProps {
  data: AppNode;
  cardHeight: number;
  gridSplit: { rows: number; cols: number };
  selectedCells: Set<string>;
  onAddNode?: (type: NodeType, position?: { x: number; y: number }, initialData?: Record<string, unknown>) => string | undefined;
  onClose: () => void;
}

export const GridSplitBar: React.FC<GridSplitBarProps> = memo(({
  data,
  cardHeight,
  gridSplit,
  selectedCells,
  onAddNode,
  onClose,
}) => {
  return (
    <InverseScaleBox
      className="absolute -top-[76px] left-1/2 flex justify-center z-30"
      style={{ transform: 'translateX(-50%)', transformOrigin: 'center bottom' }}
      scaleOrigin="center bottom"
    >
      <div className="flex items-center gap-1 nodrag bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl px-3 py-1.5 shadow-lg">
        <button
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex items-center justify-center h-9 px-2.5 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <button
          onMouseDown={(e) => e.stopPropagation()}
          className="flex items-center gap-[3px] h-9 px-2.5 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
        >
          <Grid3x3 size={16} />
          <span className="text-sm font-medium whitespace-nowrap">
            {selectedCells.size > 0 ? `已选 ${selectedCells.size} 个宫格` : '请选择宫格进行操作'}
          </span>
        </button>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={async () => {
            if (!onAddNode || selectedCells.size === 0 || !data.data.image) return;

            const sourceImage = data.data.image;
            const sourceAspectRatio = data.data.aspectRatio || DEFAULT_ASPECT_RATIO;
            const projectId = useProjectStore.getState().currentProjectId;

            const sortedCells = Array.from(selectedCells).sort((a, b) => {
              const [ar, ac] = a.split('-').map(Number);
              const [br, bc] = b.split('-').map(Number);
              return ar === br ? ac - bc : ar - br;
            });

            for (let index = 0; index < sortedCells.length; index++) {
              const cellId = sortedCells[index];
              const [row, col] = cellId.split('-').map(Number);
              const newX = data.x + 500;
              const newY = data.y + index * (cardHeight + 40);

              try {
                const croppedDataUrl = await cropCell(sourceImage, row, col, gridSplit.rows, gridSplit.cols);

                // 上传到服务器持久化存储，避免 Base64 URL 丢失
                let imageUrl = croppedDataUrl;
                if (projectId) {
                  try {
                    imageUrl = await uploadDataUrlToServer(croppedDataUrl, projectId, data.id, cellId);
                  } catch (uploadErr: any) {
                    console.error('[宫格切分] 图片上传失败，使用 Base64 回退:', uploadErr.message);
                    // 上传失败时回退使用 Base64，至少不会阻断操作
                  }
                }

                const newNodeId = onAddNode(NodeType.IMAGE_GENERATOR, { x: newX, y: newY }, {
                  image: imageUrl,
                  aspectRatio: sourceAspectRatio,
                  generationMode: 'IMG2IMG',
                  prompt: `单独生成【第${row}行第${col}列】的分镜图像，画面中不得出现任何文字、序号、编号、水印`,
                });

                if (newNodeId) {
                  useNodeStore.getState().addConnection(data.id, newNodeId);
                }
              } catch (err) {
                console.error('裁切失败:', err);
                toast.error(`宫格 ${cellId} 裁切失败`);
              }
            }

            onClose();
          }}
          className="flex items-center gap-[3px] h-9 px-2.5 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
        >
          <Sparkles size={16} />
          <span className="text-sm font-medium whitespace-nowrap">创建生图节点</span>
        </button>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <button
          onMouseDown={(e) => e.stopPropagation()}
          className="flex items-center gap-[3px] h-9 px-2.5 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
        >
          <MonitorUp size={16} />
          <span className="text-sm font-medium whitespace-nowrap">生成高清图片</span>
        </button>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <button
          onMouseDown={(e) => e.stopPropagation()}
          className="flex items-center gap-[3px] h-9 px-2.5 rounded-md bg-transparent text-white hover:text-white hover:bg-white/10 transition-all"
        >
          <span className="text-sm font-medium whitespace-nowrap">2倍</span>
          <ChevronDown size={14} className="text-white/60" />
        </button>
      </div>
    </InverseScaleBox>
  );
});

GridSplitBar.displayName = 'GridSplitBar';
